import { useState, useCallback, useRef } from 'react';
import { contactsApi } from '../../services/api';
import { logCallActivity } from '../../services/call-log/call-log.service';
import { toCallLogContactSummaries } from '../../services/call-log/call-log-contact.util';
import { logger } from '../../utils/logger';
import { normalizeSearchKey } from '../sap-contact/normalize-search-key.util';
import { ManualSearchState } from './types';

export function useManualSapContactSearch() {
  const [manualSearchState, setManualSearchState] = useState<ManualSearchState>({
    contacts: [],
    isLoading: false,
  });

  const loadedQueryRef = useRef<string | null>(null);
  const inFlightQueryRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleManualSearch = useCallback(async (params: { query: string }) => {
    const trimmedQuery = params.query.trim();
    if (!trimmedQuery) {
      return;
    }

    const searchKey = normalizeSearchKey(trimmedQuery);
    if (loadedQueryRef.current === searchKey) {
      logger.debug('[SAP C4C] Skipping duplicate manual contact search', { query: searchKey });
      return;
    }

    if (inFlightQueryRef.current === searchKey) {
      logger.debug('[SAP C4C] Manual contact search already in flight', { query: searchKey });
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    inFlightQueryRef.current = searchKey;

    setManualSearchState({ contacts: [], isLoading: true });
    try {
      const contacts = await contactsApi.search(trimmedQuery, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      loadedQueryRef.current = searchKey;
      setManualSearchState({ contacts, isLoading: false });
      logCallActivity('SAP_CONTACTS_DISPLAYED', {
        source: 'manual',
        query: searchKey,
        contactCount: contacts.length,
        contacts: toCallLogContactSummaries(contacts),
      });
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }

      setManualSearchState({
        contacts: [],
        isLoading: false,
        error: error instanceof Error ? error.message : 'Search failed',
      });
    } finally {
      if (abortControllerRef.current === controller) {
        inFlightQueryRef.current = null;
      }
    }
  }, []);

  const resetManualSearch = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    loadedQueryRef.current = null;
    inFlightQueryRef.current = null;
    setManualSearchState({ contacts: [], isLoading: false });
  }, []);

  return { manualSearchState, handleManualSearch, resetManualSearch };
}
